import type {ReactElement} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import styles from './multipage-preview.module.scss';
import {isMobile} from 'react-device-detect';
import useLongPress from '@Hooks/useLongPress';

const IMAGE_PAGE_DISPLAY_MS = 2000;

interface MultipagePreviewProps {
  previewURLs: string[];
  fallbackPreviewURLs: string[];
  videoPreviewURLs: string[];
  pagesIsVideo: boolean[];
  pageCount: number;
  onImageLoad?: () => void;
  className?: string;
}

export function MultipagePreview({previewURLs, fallbackPreviewURLs, videoPreviewURLs, pagesIsVideo, pageCount, onImageLoad, className = ''}: MultipagePreviewProps): ReactElement {
  const [isHovering, setIsHovering] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [videoPreviewReady, setVideoPreviewReady] = useState<Map<number, boolean>>(new Map());
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preloadedImagesRef = useRef<Set<string>>(new Set());
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const wrapperRef = useRef<HTMLDivElement>(null);
  const longPressHandler = useLongPress(() => {
    if (isMobile) {
      setIsHovering(true);
    }
  }, 500);

  useEffect(() => {
    const preloadImages = (): void => {
      previewURLs.forEach((url) => {
        if (url && !preloadedImagesRef.current.has(url)) {
          const img = new Image();
          img.src = url;
          preloadedImagesRef.current.add(url);
        }
      });
    };
    preloadImages();
  }, [previewURLs]);

  const advanceToNextPage = useCallback((): void => {
    if (pageCount <= 0 || pageCount <= 1 || !isHovering) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setCurrentPageIndex((prev) => (prev + 1) % pageCount);
  }, [isHovering, pageCount]);

  useEffect(() => {
    if (pageCount <= 0) {
      return;
    }

    if (!isHovering || pageCount <= 1) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (wrapperRef.current) {
        wrapperRef.current.classList.add(styles.noTransition);
        setCurrentPageIndex(0);
        requestAnimationFrame(() => {
          if (wrapperRef.current) {
            wrapperRef.current.classList.remove(styles.noTransition);
          }
        });
      } else {
        setCurrentPageIndex(0);
      }
      return;
    }

    const isCurrentPageVideo = pagesIsVideo[currentPageIndex] ?? false;

    if (isCurrentPageVideo) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      advanceToNextPage();
    }, IMAGE_PAGE_DISPLAY_MS);

    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isHovering, pageCount, currentPageIndex, pagesIsVideo]);

  useEffect(() => {
    if (pageCount <= 0 || currentPageIndex >= pageCount) {
      return;
    }

    if (isHovering && pagesIsVideo[currentPageIndex] && videoRefs.current.has(currentPageIndex)) {
      const currentVideo = videoRefs.current.get(currentPageIndex);
      if (currentVideo) {
        currentVideo.play().catch((error: unknown) => {
          console.error('Failed to play video preview:', error);
          setVideoPreviewReady((prev) => {
            const next = new Map(prev);
            next.set(currentPageIndex, false);
            return next;
          });
        });
      }
    }

    videoRefs.current.forEach((video, index) => {
      if (index !== currentPageIndex) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [isHovering, currentPageIndex, pagesIsVideo, pageCount]);

  useEffect(() => {
    return (): void => {
      videoRefs.current.forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
      videoRefs.current.clear();
    };
  }, []);

  const handleMouseEnter = (): void => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = (): void => {
    longPressHandler.onMouseLeave();
    setIsHovering(false);
  };

  const getPageCountTag = (): ReactElement => {
    return (
      <div className={`${styles.pageCountTag} radius-4 flex-center spacing-p-l-2 spacing-p-r-2 gap-1`}>
        {isHovering ? (
          <>
            <Icon className="content-body-white" icon="icon-pages" size={IconSize.SIZE_ICON_16} />
            <span className={`${styles.pageCountText} content-body-white`}>
              {currentPageIndex + 1} / {pageCount}
            </span>
          </>
        ) : (
          <>
            <Icon className="content-body-white" icon="icon-pages" size={IconSize.SIZE_ICON_16} />
            <span className={`${styles.pageCountText} content-body-white`}>{pageCount}</span>
          </>
        )}
      </div>
    );
  };

  const renderPage = useCallback(
    (index: number): ReactElement => {
      const isVideo = pagesIsVideo[index] ?? false;
      const videoSrc = videoPreviewURLs[index];
      const isCurrent = index === currentPageIndex;
      const showVideo = isHovering && isVideo && videoSrc && isCurrent;
      const thumbSrc = previewURLs[index] || fallbackPreviewURLs[index] || previewURLs[0] || fallbackPreviewURLs[0];

      const handleVideoError = (): void => {
        setVideoPreviewReady((prev) => {
          const next = new Map(prev);
          next.set(index, false);
          return next;
        });
      };

      return (
        <div key={index} className={`${styles.pageSlot} _full-width _full-height _position-relative`}>
          <img
            src={thumbSrc}
            onLoad={isCurrent ? onImageLoad : undefined}
            alt={window.i18next.t('pmwjs_template')}
            draggable="false"
            loading="lazy"
            className={`_full-width _full-height ${showVideo ? styles.hidden : ''}`}
          />
          {isVideo && videoSrc ? (
            <video
              ref={(el) => {
                if (el) {
                  videoRefs.current.set(index, el);
                } else {
                  videoRefs.current.delete(index);
                }
              }}
              src={videoSrc}
              onEnded={advanceToNextPage}
              onError={handleVideoError}
              onAbort={handleVideoError}
              className={`${styles.videoPreview} _position-absolute _full-width _full-height ${showVideo ? '' : styles.hidden}`}
              preload="none"
              muted
              playsInline
            />
          ) : null}
        </div>
      );
    },
    [isHovering, currentPageIndex, pagesIsVideo, previewURLs, fallbackPreviewURLs, videoPreviewURLs, onImageLoad, advanceToNextPage]
  );

  const renderAllPages = (): ReactElement[] => {
    const pages: ReactElement[] = [];
    for (let i = 0; i < pageCount; i++) {
      pages.push(renderPage(i));
    }
    return pages;
  };

  const getPreviewNotReadyMessage = (): ReactElement | null => {
    const currentVideoReady = videoPreviewReady.get(currentPageIndex);
    if (isHovering && pagesIsVideo[currentPageIndex] && currentVideoReady === false) {
      return <div className={`${styles.previewNotReady} body-md-bold radius-12`}>{window.i18next.t('pmwjs_preview_not_ready')}</div>;
    }
    return null;
  };

  return (
    <div
      className={`${className} _full-width _full-height _position-relative _overflow-hidden radius-12`}
      {...longPressHandler}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="_full-width _full-height _overflow-hidden _position-relative">
        <div ref={wrapperRef} className={`${styles.track} flexbox _full-height`} style={{transform: `translateX(-${currentPageIndex * 100}%)`}}>
          {renderAllPages()}
        </div>
        {getPreviewNotReadyMessage()}
      </div>
      {getPageCountTag()}
    </div>
  );
}
